import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// 修復 Leaflet 預設圖標問題
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface LeafletMapProps {
  geoJsonData?: GeoJSON.FeatureCollection;
  height?: string;
  width?: string;
  className?: string;
}

const LeafletMap: React.FC<LeafletMapProps> = ({
  geoJsonData,
  height = '400px',
  width = '100%',
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const geoJsonLayerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // 初始化地圖
    const defaultLat = parseFloat(import.meta.env.VITE_MAP_DEFAULT_LAT || '25.0330');
    const defaultLng = parseFloat(import.meta.env.VITE_MAP_DEFAULT_LNG || '121.5654');
    const defaultZoom = parseInt(import.meta.env.VITE_MAP_DEFAULT_ZOOM || '13');

    mapInstanceRef.current = L.map(mapRef.current).setView(
      [defaultLat, defaultLng],
      defaultZoom
    );

    // 添加地圖底圖
    L.tileLayer(import.meta.env.VITE_TILE_SERVER || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstanceRef.current);

    // 清理函數
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !geoJsonData) return;

    // 移除現有的 GeoJSON 層
    if (geoJsonLayerRef.current) {
      mapInstanceRef.current.removeLayer(geoJsonLayerRef.current);
    }

    // 添加新的 GeoJSON 層
    geoJsonLayerRef.current = L.geoJSON(geoJsonData, {
      style: (feature) => {
        // IMDF 檔案的樣式設定
        const featureType = feature?.properties?.category || feature?.properties?._file_type || 'default';
        
        switch (featureType) {
          case 'building':
          case 'Building':
            return { color: '#ff7800', weight: 2, fillOpacity: 0.3, fillColor: '#ff7800' };
          case 'level':
          case 'Level':
            return { color: '#0078ff', weight: 1, fillOpacity: 0.2, fillColor: '#0078ff' };
          case 'unit':
          case 'Unit':
            return { color: '#00ff78', weight: 1, fillOpacity: 0.4, fillColor: '#00ff78' };
          case 'amenity':
          case 'Amenity':
            return { color: '#ff0078', weight: 1, fillOpacity: 0.5, fillColor: '#ff0078' };
          case 'opening':
          case 'Opening':
            return { color: '#ffaa00', weight: 1, fillOpacity: 0.6, fillColor: '#ffaa00' };
          case 'fixture':
          case 'Fixture':
            return { color: '#aa00ff', weight: 1, fillOpacity: 0.4, fillColor: '#aa00ff' };
          case 'anchor':
          case 'Anchor':
            return { color: '#00aaff', weight: 3, fillOpacity: 0.8, fillColor: '#00aaff' };
          default:
            return { color: '#3388ff', weight: 2, fillOpacity: 0.3, fillColor: '#3388ff' };
        }
      },
      onEachFeature: (feature, layer) => {
        // 添加彈出視窗顯示特徵資訊
        if (feature.properties) {
          const filteredProperties = Object.entries(feature.properties)
            .filter(([key]) => !key.startsWith('_')) // 過濾掉內部屬性
            .slice(0, 10); // 限制顯示數量避免過長
          
          const popupContent = `
            <div style="max-width: 300px;">
              <h4 style="margin: 0 0 8px 0; color: #1f2937; font-weight: bold;">
                ${feature.properties._file_type || '特徵'} 資訊
              </h4>
              ${filteredProperties
                .map(([key, value]) => `
                  <div style="margin-bottom: 4px;">
                    <strong style="color: #374151;">${key}:</strong> 
                    <span style="color: #6b7280;">${value || 'N/A'}</span>
                  </div>
                `).join('')}
              ${feature.properties._source_file ? `
                <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                  <small style="color: #9ca3af;">來源: ${feature.properties._source_file}</small>
                </div>
              ` : ''}
            </div>
          `;
          layer.bindPopup(popupContent);
        }
      },
    }).addTo(mapInstanceRef.current);

    // 調整地圖視圖以顯示所有 GeoJSON 特徵
    const bounds = geoJsonLayerRef.current.getBounds();
    if (bounds.isValid()) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [geoJsonData]);

  return (
    <div
      ref={mapRef}
      className={`leaflet-map ${className}`}
      style={{ height, width }}
    />
  );
};

export default LeafletMap;